import type { Pool, PoolClient } from "pg";

import { skuRequiresImeiIccid } from "../../manufacturing/configuration-sku-family.js";
import {
  isSuntronicPcbaSerial,
  parseKodiakPcbaPrefixFromSerial,
} from "../../mrp/kodiak-pcba-prefix-planned.js";
import { sqlQualityIncidentLinksSerialFci } from "../../qc/quality-incident-guards.js";
import { presentApprovalBoardColumn } from "../../sales-order/approval-ticket-status.js";
import {
  parseOptionalId,
  parseOptionalVoltage,
  trimOptionalText,
  trimRequiredText,
} from "./quality-corrective-action-helpers.js";
import { parseInboundArrivalPackageHex } from "../shipping-inbound/shipping-inbound-arrival-package-scan.js";
import { chicagoYearMonth, nextRmaNumber } from "./quality-rma-number.js";

export const RMA_STATUSES = [
  "awaiting_return",
  "received",
  "closed",
  "cancelled",
] as const;

export type RmaStatus = (typeof RMA_STATUSES)[number];

export const RMA_LED_BY = ["customer", "end_user"] as const;

export type RmaLedBy = (typeof RMA_LED_BY)[number];

const RMA_STATUS_SET = new Set<string>(RMA_STATUSES);
const RMA_LED_BY_SET = new Set<string>(RMA_LED_BY);

export type RmaQuantityModelLine = {
  quantity: string;
  models: string;
  reason: string;
};

const REASON_MARK = " | ";

function detachReason(text: string): { body: string; reason: string } {
  if (text.startsWith("| ")) {
    return { body: "", reason: text.slice(2).trim() };
  }
  const index = text.indexOf(REASON_MARK);
  if (index < 0) return { body: text, reason: "" };
  return { body: text.slice(0, index).trim(), reason: text.slice(index + REASON_MARK.length).trim() };
}

function attachReason(body: string, reason: string): string {
  const note = reason.trim();
  if (note === "") return body;
  if (body.trim() === "") return `| ${note}`;
  return `${body}${REASON_MARK}${note}`;
}

export function splitRmaQuantityAndModels(raw: string | null | undefined): RmaQuantityModelLine {
  const text = (raw ?? "").trim();
  if (text === "") {
    return { quantity: "", models: "", reason: "" };
  }
  const { body, reason } = detachReason(text);
  const match = body.match(/^(\d+)\s*[x×]\s*(.+)$/i);
  if (match) {
    return { quantity: match[1], models: match[2].trim(), reason };
  }
  return { quantity: "", models: body.trim(), reason };
}

export function joinRmaQuantityAndModels(quantity: string, models: string): string | null {
  const qty = quantity.trim();
  const model = models.trim();
  if (qty === "" && model === "") {
    return null;
  }
  if (qty !== "" && model !== "") {
    return `${qty}x ${model}`;
  }
  if (qty !== "") {
    return qty;
  }
  return model;
}

export function parseRmaQuantityModelLines(raw: string | null | undefined): RmaQuantityModelLine[] {
  const text = (raw ?? "").replace(/\r\n/g, "\n").trim();
  if (text === "") {
    return [];
  }
  return text
    .split("\n")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => splitRmaQuantityAndModels(part));
}

export function formatRmaQuantityModelLines(lines: readonly RmaQuantityModelLine[]): string | null {
  const parts: string[] = [];
  for (const line of lines) {
    const reason = line.reason?.trim() ?? "";
    if (line.models.trim() === "" && reason === "") {
      continue;
    }
    const joined = joinRmaQuantityAndModels(line.quantity, line.models);
    const withReason = attachReason(joined ?? "", reason);
    if (withReason.trim() !== "") {
      parts.push(withReason);
    }
  }
  return parts.length === 0 ? null : parts.join("\n");
}

export function sumRmaOrderedDeviceCount(quantityAndModels: string | null | undefined): number {
  return parseRmaQuantityModelLines(quantityAndModels).reduce((sum, line) => {
    const parsed = Number.parseInt(line.quantity.trim(), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return sum;
    }
    return sum + parsed;
  }, 0);
}

export interface QualityRmaRow {
  id: string | number;
  rma_number: string;
  status: string;
  led_by: RmaLedBy;
  requested_at: string | Date | null;
  approved_at: string | Date | null;
  complaint: string | null;
  sgs_cause_of_return: string | null;
  quantity_and_models: string | null;
  purchase_reference: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  ship_from_line1: string | null;
  ship_from_line2: string | null;
  ship_from_city: string | null;
  ship_from_state: string | null;
  ship_from_postal_code: string | null;
  ship_from_country: string | null;
  ship_to_line1: string | null;
  ship_to_line2: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_postal_code: string | null;
  ship_to_country: string | null;
  customer_fs_account_id: string | number | null;
  customer_name: string | null;
  end_user_fs_account_id: string | number | null;
  end_user_name: string | null;
  created_by: string;
  created_by_display: string | null;
  created_at: string | Date;
  updated_at: string | Date;
  intake_count: string | number;
  investigation_count: string | number;
  receive_quantity_discrepancy?: boolean | null;
  sales_orders?: unknown;
}

export interface QualityRma {
  id: number;
  rmaNumber: string;
  status: RmaStatus;
  ledBy: RmaLedBy;
  requestedAt: string | null;
  approvedAt: string | null;
  complaint: string | null;
  sgsCauseOfReturn: string | null;
  quantityAndModels: string | null;
  purchaseReference: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  shipFromLine1: string | null;
  shipFromLine2: string | null;
  shipFromCity: string | null;
  shipFromState: string | null;
  shipFromPostalCode: string | null;
  shipFromCountry: string | null;
  shipToLine1: string | null;
  shipToLine2: string | null;
  shipToCity: string | null;
  shipToState: string | null;
  shipToPostalCode: string | null;
  shipToCountry: string | null;
  customerFsAccountId: number | null;
  customerName: string | null;
  endUserFsAccountId: number | null;
  endUserName: string | null;
  createdBy: string;
  createdByDisplay: string | null;
  createdAt: string;
  updatedAt: string;
  intakeCount: number;
  investigationCount: number;
  receiveQuantityDiscrepancy: boolean;
  salesOrders: QualityRmaSalesOrder[];
}

export interface QualityRmaSalesOrder {
  salesOrderId: number;
  salesOrderNumber: string | null;
}

export interface QualityRmaApproval {
  approvalTicketId: number;
  name: string;
  type: string;
  columnKey: string;
  referenceNumber: string | null;
  salesOrderId: number | null;
  salesOrderNumber: string | null;
}

export interface QualityRmaDevice {
  id: number;
  deviceId: number | null;
  serialFciId: number | null;
  deviceLabelSerial: string;
  finishedSku: string | null;
  firmwareVersion: string | null;
  communicating: boolean;
  pcbaLocationId: number | null;
  qualityIncidentId: number | null;
  qualityIncidentRepairScrap: string | null;
  qualityIncidentOpen: boolean;
}

export interface QualityRmaIntake {
  id: number;
  receivedAt: string | null;
  unitGroup: string | null;
  unitModel: string | null;
  unitSerial: string | null;
  pcbaLocationId: number | null;
  visualInspection: string | null;
  dateOfManufacture: string | null;
  aaVoltage: string | null;
  cVoltage: string | null;
  hybridCap: string | null;
  firmwareVersion: string | null;
  boardRevision: string | null;
  loggedBy: string | null;
  boxLabel: string | null;
  rcaDate: string | null;
  rcaNotes: string | null;
  resolution: string | null;
  analyzedBy: string | null;
  resolutionDate: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QualityRca {
  id: number;
  /** Owning RMA. Null when the RCA was opened from an NCR, event, or case. */
  rmaId: number | null;
  status: "open" | "closed";
  openedAt: string;
  openedBy: string;
  openedByDisplay: string | null;
  openedByAvatarUrl: string | null;
  openedAutomatically: boolean;
  summary: string | null;
  form: Record<string, unknown> | null;
}

export interface QualityRmaInvestigationLink {
  investigationId: number;
  qualityCaseId: number;
  investigationStatus: string;
  caseTitle: string;
}

export interface QualityRmaUpsLabel {
  id: number;
  trackingNumber: string;
  serviceCode: string | null;
  createdBy: string;
  createdAt: string;
  voidedAt: string | null;
}

export interface QualityRcaLinkedRma {
  rcaId: number;
  id: number;
  rmaNumber: string;
  status: RmaStatus;
  customerName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  dateReceived: string | null;
  quantityAndModels: string | null;
  complaint: string | null;
}

export interface QualityRmaDetail {
  rma: QualityRma;
  salesOrders: QualityRmaSalesOrder[];
  approvals: QualityRmaApproval[];
  freshdeskTicketIds: number[];
  devices: QualityRmaDevice[];
  intake: QualityRmaIntake[];
  rcas: QualityRca[];
  investigations: QualityRmaInvestigationLink[];
  upsLabels: QualityRmaUpsLabel[];
  /** Every RMA that owns or links an RCA shown on this RMA. */
  rcaLinkedRmas?: QualityRcaLinkedRma[];
}

export interface QualityRmaListInput {
  limit?: unknown;
  offset?: unknown;
  status?: unknown;
  search?: unknown;
  ledBy?: unknown;
}

function isoTimestampText(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function dateOnlyText(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = String(value.getFullYear());
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const text = String(value).trim();
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(text);
  if (iso) return iso[1];
  return text.slice(0, 10);
}

function optionalPgId(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Older rows used investigating. Received is the last active RMA status. */
export function presentRmaStatus(status: string): RmaStatus {
  if (status === "investigating") return "received";
  if (RMA_STATUS_SET.has(status)) return status as RmaStatus;
  throw new Error(`Unexpected RMA status: ${status}`);
}

export function rmaStatusLabel(status: RmaStatus): string {
  switch (status) {
    case "awaiting_return":
      return "Awaiting return";
    case "received":
      return "Received";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function rmaLedByLabel(ledBy: RmaLedBy): string {
  switch (ledBy) {
    case "customer":
      return "Customer-led";
    case "end_user":
      return "End-user-led";
    default:
      return ledBy;
  }
}

export function parseRmaStatus(value: unknown): RmaStatus | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value !== "string") {
    throw new Error("status must be a string.");
  }
  const normalized = value.trim().toLowerCase();
  if (!RMA_STATUS_SET.has(normalized)) {
    throw new Error(`status must be one of: ${RMA_STATUSES.join(", ")}.`);
  }
  return normalized as RmaStatus;
}

export function parseRequiredRmaStatus(value: unknown): RmaStatus {
  const parsed = parseRmaStatus(value);
  if (parsed == null) {
    throw new Error(`status must be one of: ${RMA_STATUSES.join(", ")}.`);
  }
  return parsed;
}

export function parseRmaLedBy(value: unknown): RmaLedBy | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value !== "string") {
    throw new Error("ledBy must be a string.");
  }
  const normalized = value.trim().toLowerCase();
  if (!RMA_LED_BY_SET.has(normalized)) {
    throw new Error(`ledBy must be one of: ${RMA_LED_BY.join(", ")}.`);
  }
  return normalized as RmaLedBy;
}

export function parseRequiredRmaLedBy(value: unknown): RmaLedBy {
  const parsed = parseRmaLedBy(value);
  if (parsed == null) {
    throw new Error(`ledBy must be one of: ${RMA_LED_BY.join(", ")}.`);
  }
  return parsed;
}

export function parseRmaId(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error("RMA id must be a positive integer.");
  }
  return parsed;
}

export function parseRmaLimit(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return 100;
  return Math.min(Math.trunc(parsed), 500);
}

export function parseRmaOffset(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.trunc(parsed);
}

export function parseFreshdeskTicketId(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error("Freshdesk ticket id must be a positive integer.");
  }
  return Math.trunc(parsed);
}

export function parseQualityRmaSalesOrders(value: unknown): QualityRmaSalesOrder[] {
  if (!Array.isArray(value)) return [];
  const salesOrders: QualityRmaSalesOrder[] = [];
  for (const entry of value) {
    if (entry == null || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const salesOrderId = Number(record.salesOrderId);
    if (!Number.isFinite(salesOrderId) || salesOrderId <= 0) continue;
    const rawNumber = record.salesOrderNumber;
    salesOrders.push({
      salesOrderId,
      salesOrderNumber: typeof rawNumber === "string" && rawNumber.trim() !== "" ? rawNumber : null,
    });
  }
  return salesOrders;
}

export function mapQualityRmaRow(row: QualityRmaRow): QualityRma {
  return {
    id: Number(row.id),
    rmaNumber: row.rma_number,
    status: presentRmaStatus(row.status),
    ledBy: row.led_by,
    requestedAt: dateOnlyText(row.requested_at),
    approvedAt: dateOnlyText(row.approved_at),
    complaint: row.complaint,
    sgsCauseOfReturn: row.sgs_cause_of_return,
    quantityAndModels: row.quantity_and_models,
    purchaseReference: row.purchase_reference,
    contactName: row.contact_name,
    contactEmail: row.contact_email,
    contactPhone: row.contact_phone,
    shipFromLine1: row.ship_from_line1,
    shipFromLine2: row.ship_from_line2,
    shipFromCity: row.ship_from_city,
    shipFromState: row.ship_from_state,
    shipFromPostalCode: row.ship_from_postal_code,
    shipFromCountry: row.ship_from_country,
    shipToLine1: row.ship_to_line1,
    shipToLine2: row.ship_to_line2,
    shipToCity: row.ship_to_city,
    shipToState: row.ship_to_state,
    shipToPostalCode: row.ship_to_postal_code,
    shipToCountry: row.ship_to_country,
    customerFsAccountId: optionalPgId(row.customer_fs_account_id),
    customerName: row.customer_name,
    endUserFsAccountId: optionalPgId(row.end_user_fs_account_id),
    endUserName: row.end_user_name,
    createdBy: row.created_by,
    createdByDisplay: row.created_by_display,
    createdAt: isoTimestampText(row.created_at) ?? "",
    updatedAt: isoTimestampText(row.updated_at) ?? "",
    intakeCount: Number(row.intake_count) || 0,
    investigationCount: Number(row.investigation_count) || 0,
    receiveQuantityDiscrepancy: row.receive_quantity_discrepancy === true,
    salesOrders: parseQualityRmaSalesOrders(row.sales_orders),
  };
}

const RMA_SELECT = `
         quality.rma.id,
         quality.rma.rma_number,
         quality.rma.status,
         quality.rma.led_by,
         quality.rma.requested_at,
         quality.rma.approved_at,
         quality.rma.complaint,
         quality.rma.sgs_cause_of_return,
         quality.rma.quantity_and_models,
         quality.rma.receive_quantity_discrepancy,
         quality.rma.purchase_reference,
         quality.rma.contact_name,
         quality.rma.contact_email,
         quality.rma.contact_phone,
         quality.rma.ship_from_line1,
         quality.rma.ship_from_line2,
         quality.rma.ship_from_city,
         quality.rma.ship_from_state,
         quality.rma.ship_from_postal_code,
         quality.rma.ship_from_country,
         quality.rma.ship_to_line1,
         quality.rma.ship_to_line2,
         quality.rma.ship_to_city,
         quality.rma.ship_to_state,
         quality.rma.ship_to_postal_code,
         quality.rma.ship_to_country,
         quality.rma.customer_fs_account_id,
         customer_account.fs_account_name as customer_name,
         quality.rma.end_user_fs_account_id,
         coalesce(
           nullif(trim(end_user_account.fs_account_name), '')
         ,
           nullif(trim(end_user_account.mv_end_user_name), '')) as end_user_name,
         quality.rma.created_by,
         coalesce(
           nullif(trim(created_user_setting.display_name), ''),
           trim(created_auth_user.email),
           nullif(trim(quality.rma.created_by), '')
         ) as created_by_display,
         quality.rma.created_at,
         quality.rma.updated_at,
         (
           select count(*)::bigint
             from quality.rma_intake
            where quality.rma_intake.rma_id = quality.rma.id
         ) as intake_count,
         (
           select count(*)::bigint
             from quality.rma_investigation
            where quality.rma_investigation.rma_id = quality.rma.id
         ) as investigation_count,
         (
           select coalesce(
             json_agg(
               json_build_object(
                 'salesOrderId', quality.rma_sales_order.sales_order_id,
                 'salesOrderNumber', public.sales_order.number
               )
               order by public.sales_order.number
             ),
             '[]'::json
           )
             from quality.rma_sales_order
             left join public.sales_order
               on public.sales_order.id = quality.rma_sales_order.sales_order_id
            where quality.rma_sales_order.rma_id = quality.rma.id
         ) as sales_orders
    from quality.rma
    left join client.data as customer_account
      on customer_account.fs_account_id = quality.rma.customer_fs_account_id
    left join client.data as end_user_account
      on end_user_account.fs_account_id = quality.rma.end_user_fs_account_id
    left join auth.users as created_auth_user
      on lower(trim(created_auth_user.email)) = lower(trim(quality.rma.created_by))
    left join public.user_setting as created_user_setting
      on created_user_setting.id = created_auth_user.id
`;

export async function allocateNextRmaNumber(client: Pool | PoolClient, at: Date = new Date()): Promise<string> {
  const { year, month } = chicagoYearMonth(at);
  const prefix = `RMA-${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-`;
  await client.query(`select pg_advisory_xact_lock(hashtext($1))`, [`quality.rma:${prefix}`]);
  const result = await client.query<{ rma_number: string }>(
    `select quality.rma.rma_number
       from quality.rma
      where quality.rma.rma_number like $1`,
    [`${prefix}%`],
  );
  return nextRmaNumber(
    result.rows.map((row) => row.rma_number),
    year,
    month,
  );
}

export async function loadQualityRmas(
  pool: Pool,
  input: QualityRmaListInput,
): Promise<{ rows: QualityRma[]; total: number }> {
  const limit = parseRmaLimit(input.limit);
  const offset = parseRmaOffset(input.offset);
  const status = parseRmaStatus(input.status);
  const ledBy = parseRmaLedBy(input.ledBy);
  const search = trimOptionalText(input.search, "search", 200);

  const conditions: string[] = [];
  const params: unknown[] = [];
  let paramIndex = 1;

  if (status != null) {
    conditions.push(`quality.rma.status = $${paramIndex}`);
    params.push(status);
    paramIndex += 1;
  }
  if (ledBy != null) {
    conditions.push(`quality.rma.led_by = $${paramIndex}`);
    params.push(ledBy);
    paramIndex += 1;
  }
  if (search != null) {
    conditions.push(
      `(quality.rma.rma_number ilike $${paramIndex}
        or coalesce(quality.rma.complaint, '') ilike $${paramIndex}
        or coalesce(quality.rma.quantity_and_models, '') ilike $${paramIndex}
        or coalesce(customer_account.fs_account_name, '') ilike $${paramIndex}
        or coalesce(end_user_account.fs_account_name, '') ilike $${paramIndex}
        or coalesce(end_user_account.mv_end_user_name, '') ilike $${paramIndex}
        or exists (
          select 1
            from quality.rma_sales_order
            join public.sales_order
              on public.sales_order.id = quality.rma_sales_order.sales_order_id
           where quality.rma_sales_order.rma_id = quality.rma.id
             and public.sales_order.number ilike $${paramIndex}
        ))`,
    );
    params.push(`%${search}%`);
    paramIndex += 1;
  }

  const whereClause = conditions.length > 0 ? `where ${conditions.join(" and ")}` : "";

  const countResult = await pool.query<{ total_count: string | number }>(
    `select count(*)::bigint as total_count
       from quality.rma
       left join client.data as customer_account
         on customer_account.fs_account_id = quality.rma.customer_fs_account_id
       left join client.data as end_user_account
         on end_user_account.fs_account_id = quality.rma.end_user_fs_account_id
       ${whereClause}`,
    params,
  );

  const listParams = [...params, limit, offset];
  const listResult = await pool.query<QualityRmaRow>(
    `select ${RMA_SELECT}
      ${whereClause}
     order by quality.rma.rma_number desc, quality.rma.id desc
     limit $${paramIndex} offset $${paramIndex + 1}`,
    listParams,
  );

  return {
    rows: listResult.rows.map(mapQualityRmaRow),
    total: Number(countResult.rows[0]?.total_count) || 0,
  };
}

/** Arrival sticker barcode: hub `/a/{hex}` URL, dashed UUID, or bare 32 hex. */
export function arrivalScanHexFromSearch(raw: string): string | null {
  return parseInboundArrivalPackageHex(raw);
}

export async function resolveQualityRmaFromArrivalScan(
  pool: Pool,
  scanHex: string,
): Promise<{ id: number; rmaNumber: string } | null> {
  const result = await pool.query<{ id: string | number; rma_number: string }>(
    `select quality.rma.id,
            quality.rma.rma_number
       from public.inbound_arrival_package
       join public.inbound_arrival
         on public.inbound_arrival.id = public.inbound_arrival_package.inbound_arrival_id
       join quality.rma
         on upper(trim(quality.rma.rma_number)) = upper(trim(public.inbound_arrival.po_number))
      where replace(public.inbound_arrival_package.scan_token::text, '-', '') = $1
      limit 1`,
    [scanHex],
  );
  const row = result.rows[0];
  if (row == null) return null;
  const id = Number(row.id);
  if (!Number.isSafeInteger(id) || id < 1) return null;
  return { id, rmaNumber: row.rma_number };
}

export async function loadQualityRmaHeader(
  pool: Pool | PoolClient,
  rmaId: number,
): Promise<QualityRma | null> {
  const result = await pool.query<QualityRmaRow>(
    `select ${RMA_SELECT}
      where quality.rma.id = $1`,
    [rmaId],
  );
  const row = result.rows[0];
  return row ? mapQualityRmaRow(row) : null;
}

const SUNTRONIC_LOCATION_ID = 2;
const KODIAK_LOCATION_ID = 14;

/** Kodiak or Suntronic from the PCBA build-order site, otherwise from the PCBA barcode. */
export function pcbaManufacturerLocationId(
  pcbaSerial: string | null | undefined,
  buildOrderLocationId: number | null,
): number | null {
  if (buildOrderLocationId === SUNTRONIC_LOCATION_ID || buildOrderLocationId === KODIAK_LOCATION_ID) {
    return buildOrderLocationId;
  }
  const serial = (pcbaSerial ?? "").trim();
  if (serial === "") return null;
  if (isSuntronicPcbaSerial(serial)) return SUNTRONIC_LOCATION_ID;
  if (parseKodiakPcbaPrefixFromSerial(serial) != null) return KODIAK_LOCATION_ID;
  return null;
}

/** PCBA manufacturer site for a device label. Kodiak and Suntronic are the current board houses. */
async function loadPcbaManufacturerLocationBySerial(
  pool: Pool | PoolClient,
  serials: Array<string | null | undefined>,
): Promise<Map<string, number>> {
  const keys = [
    ...new Set(serials.map((serial) => (serial ?? "").trim().toUpperCase()).filter((serial) => serial !== "")),
  ];
  const locations = new Map<string, number>();
  if (keys.length === 0) return locations;
  const result = await pool.query<{
    serial_key: string;
    pcba_serial: string | null;
    pcba_location_id: string | number | null;
  }>(
    `select distinct on (upper(btrim(public.serial_fci.device_label_serial)))
            upper(btrim(public.serial_fci.device_label_serial)) as serial_key,
            public.serial_fci.pcba_serial,
            public.build_order.location_id as pcba_location_id
       from public.serial_fci
       left join public.build_order
         on public.build_order.id = public.serial_fci.pcba_build_order_id
      where upper(btrim(public.serial_fci.device_label_serial)) = any($1::text[])
        and public.serial_fci.archived_at is null
      order by upper(btrim(public.serial_fci.device_label_serial)),
               public.serial_fci.id desc`,
    [keys],
  );
  for (const row of result.rows) {
    const locationId = pcbaManufacturerLocationId(row.pcba_serial, optionalPgId(row.pcba_location_id));
    if (locationId != null) locations.set(row.serial_key, locationId);
  }
  return locations;
}

export async function loadQualityRmaDetail(
  pool: Pool | PoolClient,
  rmaId: number,
): Promise<QualityRmaDetail | null> {
  const rma = await loadQualityRmaHeader(pool, rmaId);
  if (rma == null) return null;

  const [salesOrders, tickets, approvals, devices, intake, rcas, investigations, upsLabels, rcaLinkedRmas] = await Promise.all([
    pool.query<{ sales_order_id: string | number; sales_order_number: string | null }>(
      `select quality.rma_sales_order.sales_order_id,
              public.sales_order.number as sales_order_number
         from quality.rma_sales_order
         left join public.sales_order
           on public.sales_order.id = quality.rma_sales_order.sales_order_id
        where quality.rma_sales_order.rma_id = $1
        order by public.sales_order.number`,
      [rmaId],
    ),
    pool.query<{ freshdesk_ticket_id: string | number }>(
      `select quality.rma_freshdesk_ticket.freshdesk_ticket_id
         from quality.rma_freshdesk_ticket
        where quality.rma_freshdesk_ticket.rma_id = $1
        order by quality.rma_freshdesk_ticket.freshdesk_ticket_id`,
      [rmaId],
    ),
    pool.query<{
      approval_ticket_id: string | number;
      name: string;
      type: string;
      column_key: string;
      reference_number: string | null;
      sales_order_id: string | number | null;
      sales_order_number: string | null;
    }>(
      `select quality.rma_approval.approval_ticket_id,
              inside_sale.approval_ticket.name,
              inside_sale.approval_ticket.type,
              inside_sale.approval_ticket.column_key,
              inside_sale.approval_ticket.reference_number,
              inside_sale.approval_ticket.sales_order_id,
              public.sales_order.number as sales_order_number
         from quality.rma_approval
         join inside_sale.approval_ticket
           on inside_sale.approval_ticket.id = quality.rma_approval.approval_ticket_id
         left join public.sales_order
           on public.sales_order.id = inside_sale.approval_ticket.sales_order_id
        where quality.rma_approval.rma_id = $1
        order by quality.rma_approval.created_at desc, quality.rma_approval.approval_ticket_id desc`,
      [rmaId],
    ),
    pool.query<{
      id: string | number;
      device_id: string | number | null;
      serial_fci_id: string | number | null;
      device_label_serial: string;
      finished_sku: string | null;
      firmware_version: string | null;
      quality_incident_id: string | number | null;
      repair_scrap: string | null;
      resolution_date: string | Date | null;
    }>(
      `select quality.rma_device.id,
              quality.rma_device.device_id,
              quality.rma_device.serial_fci_id,
              quality.rma_device.device_label_serial,
              live_episode.finished_sku,
              live_episode.firmware_version,
              latest_incident.id as quality_incident_id,
              latest_incident.repair_scrap,
              latest_incident.resolution_date
         from quality.rma_device
         left join lateral (
           select public.serial_fci.id,
                  public.serial_fci.finished_sku,
                  public.serial_fci.firmware_version,
                  public.serial_fci.clamp_serial,
                  public.serial_fci.pcba_serial
             from public.serial_fci
            where (
                    quality.rma_device.serial_fci_id is not null
                    and public.serial_fci.id = quality.rma_device.serial_fci_id
                  )
               or (
                    quality.rma_device.serial_fci_id is null
                    and public.serial_fci.device_label_serial = quality.rma_device.device_label_serial
                    and public.serial_fci.archived_at is null
                  )
            order by public.serial_fci.archived_at nulls first, public.serial_fci.id desc
            limit 1
         ) as live_episode on true
         left join lateral (
           select public.quality_incident.id,
                  public.quality_incident.repair_scrap,
                  public.quality_incident.resolution_date
             from public.quality_incident
            where live_episode.id is not null
              and ${sqlQualityIncidentLinksSerialFci("public.quality_incident", "live_episode")}
            order by public.quality_incident.resolution_date nulls first,
                     public.quality_incident.id desc
            limit 1
         ) as latest_incident on true
        where quality.rma_device.rma_id = $1
        order by quality.rma_device.id`,
      [rmaId],
    ),
    pool.query<{
      id: string | number;
      received_at: string | Date | null;
      unit_group: string | null;
      unit_model: string | null;
      unit_serial: string | null;
      visual_inspection: string | null;
      date_of_manufacture: string | Date | null;
      aa_voltage: string | null;
      c_voltage: string | null;
      hybrid_cap: string | null;
      firmware_version: string | null;
      board_revision: string | null;
      logged_by: string | null;
      box_label: string | null;
      rca_date: string | Date | null;
      rca_notes: string | null;
      resolution: string | null;
      analyzed_by: string | null;
      resolution_date: string | Date | null;
      resolved_by: string | null;
      created_at: string | Date;
      updated_at: string | Date;
    }>(
      `select quality.rma_intake.id,
              quality.rma_intake.received_at,
              quality.rma_intake.unit_group,
              quality.rma_intake.unit_model,
              quality.rma_intake.unit_serial,
              quality.rma_intake.visual_inspection,
              quality.rma_intake.date_of_manufacture,
              quality.rma_intake.aa_voltage,
              quality.rma_intake.c_voltage,
              quality.rma_intake.hybrid_cap,
              quality.rma_intake.firmware_version,
              quality.rma_intake.board_revision,
              quality.rma_intake.logged_by,
              quality.rma_intake.box_label,
              quality.rma_intake.rca_date,
              quality.rma_intake.rca_notes,
              quality.rma_intake.resolution,
              quality.rma_intake.analyzed_by,
              quality.rma_intake.resolution_date,
              quality.rma_intake.resolved_by,
              quality.rma_intake.created_at,
              quality.rma_intake.updated_at
         from quality.rma_intake
        where quality.rma_intake.rma_id = $1
        order by quality.rma_intake.received_at desc nulls last, quality.rma_intake.id`,
      [rmaId],
    ),
    pool.query<{
      id: string | number;
      rma_id: string | number;
      status: string;
      opened_at: string | Date;
      opened_by: string;
      opened_by_display: string | null;
      opened_by_avatar_url: string | null;
      opened_automatically: boolean;
      summary: string | null;
      form: Record<string, unknown> | null;
    }>(
      `select quality.rca.id,
              quality.rca.rma_id,
              quality.rca.status,
              quality.rca.opened_at,
              quality.rca.opened_by,
              coalesce(
                nullif(trim(public.user_setting.display_name), ''),
                trim(auth.users.email),
                nullif(trim(quality.rca.opened_by), '')
              ) as opened_by_display,
              public.user_setting.avatar_url as opened_by_avatar_url,
              quality.rca.opened_automatically,
              quality.rca.summary,
              quality.rca.form
         from quality.rca
         left join auth.users
           on lower(trim(auth.users.email)) = lower(trim(quality.rca.opened_by))
         left join public.user_setting
           on public.user_setting.id = auth.users.id
        where quality.rca.rma_id = $1
           or exists (
             select 1
               from quality.rma_rca_link
              where quality.rma_rca_link.rma_id = $1
                and quality.rma_rca_link.rca_id = quality.rca.id
           )
        order by quality.rca.opened_at, quality.rca.id`,
      [rmaId],
    ),
    pool.query<{
      investigation_id: string | number;
      quality_case_id: string | number;
      investigation_status: string;
      case_title: string;
    }>(
      `select quality.rma_investigation.investigation_id,
              quality.investigation.quality_case_id,
              quality.investigation.status as investigation_status,
              quality.quality_case.title as case_title
         from quality.rma_investigation
         join quality.investigation
           on quality.investigation.id = quality.rma_investigation.investigation_id
         join quality.quality_case
           on quality.quality_case.id = quality.investigation.quality_case_id
        where quality.rma_investigation.rma_id = $1
        order by quality.rma_investigation.investigation_id desc`,
      [rmaId],
    ),
    pool.query<{
      id: string | number;
      tracking_number: string;
      service_code: string | null;
      created_by: string;
      created_at: string | Date;
      voided_at: string | Date | null;
    }>(
      `select quality.rma_ups_label.id,
              quality.rma_ups_label.tracking_number,
              quality.rma_ups_label.service_code,
              quality.rma_ups_label.created_by,
              quality.rma_ups_label.created_at,
              quality.rma_ups_label.voided_at
         from quality.rma_ups_label
        where quality.rma_ups_label.rma_id = $1
        order by quality.rma_ups_label.created_at desc`,
      [rmaId],
    ),
    pool.query<{
      rca_id: string | number;
      id: string | number;
      rma_number: string;
      status: string;
      contact_name: string | null;
      contact_phone: string | null;
      quantity_and_models: string | null;
      complaint: string | null;
      customer_name: string | null;
      date_received: string | Date | null;
    }>(
      `select quality.rca.id as rca_id,
              quality.rma.id,
              quality.rma.rma_number,
              quality.rma.status,
              quality.rma.contact_name,
              quality.rma.contact_phone,
              quality.rma.quantity_and_models,
              quality.rma.complaint,
              client.data.fs_account_name as customer_name,
              (
                select min(quality.rma_intake.received_at)
                  from quality.rma_intake
                 where quality.rma_intake.rma_id = quality.rma.id
              ) as date_received
         from quality.rca
         join quality.rma
           on quality.rma.id = quality.rca.rma_id
         left join client.data
           on client.data.fs_account_id = quality.rma.customer_fs_account_id
        where quality.rca.id in (
          select quality.rca.id
            from quality.rca
           where quality.rca.rma_id = $1
              or exists (
                select 1
                  from quality.rma_rca_link
                 where quality.rma_rca_link.rma_id = $1
                   and quality.rma_rca_link.rca_id = quality.rca.id
              )
        )
      union
      select quality.rma_rca_link.rca_id,
             quality.rma.id,
             quality.rma.rma_number,
             quality.rma.status,
             quality.rma.contact_name,
             quality.rma.contact_phone,
             quality.rma.quantity_and_models,
             quality.rma.complaint,
             client.data.fs_account_name as customer_name,
             (
               select min(quality.rma_intake.received_at)
                 from quality.rma_intake
                where quality.rma_intake.rma_id = quality.rma.id
             ) as date_received
        from quality.rma_rca_link
        join quality.rma
          on quality.rma.id = quality.rma_rca_link.rma_id
        left join client.data
          on client.data.fs_account_id = quality.rma.customer_fs_account_id
       where quality.rma_rca_link.rca_id in (
         select quality.rca.id
           from quality.rca
          where quality.rca.rma_id = $1
             or exists (
               select 1
                 from quality.rma_rca_link
                where quality.rma_rca_link.rma_id = $1
                  and quality.rma_rca_link.rca_id = quality.rca.id
             )
       )
       order by rma_number`,
      [rmaId],
    ),
  ]);

  const pcbaLocationBySerial = await loadPcbaManufacturerLocationBySerial(pool, [
    ...devices.rows.map((row) => row.device_label_serial),
    ...intake.rows.map((row) => row.unit_serial),
  ]);

  return {
    rma,
    salesOrders: salesOrders.rows.map((row) => ({
      salesOrderId: Number(row.sales_order_id),
      salesOrderNumber: row.sales_order_number,
    })),
    approvals: approvals.rows.map((row) => ({
      approvalTicketId: Number(row.approval_ticket_id),
      name: row.name,
      type: row.type,
      columnKey: presentApprovalBoardColumn(row.column_key),
      referenceNumber: row.reference_number,
      salesOrderId: optionalPgId(row.sales_order_id),
      salesOrderNumber: row.sales_order_number,
    })),
    freshdeskTicketIds: tickets.rows
      .map((row) => Number(row.freshdesk_ticket_id))
      .filter((id) => Number.isFinite(id)),
    devices: devices.rows.map((row) => ({
      id: Number(row.id),
      deviceId: optionalPgId(row.device_id),
      serialFciId: optionalPgId(row.serial_fci_id),
      deviceLabelSerial: row.device_label_serial,
      finishedSku: row.finished_sku,
      firmwareVersion: row.firmware_version,
      communicating: skuRequiresImeiIccid(row.finished_sku),
      pcbaLocationId: pcbaLocationBySerial.get(row.device_label_serial.trim().toUpperCase()) ?? null,
      qualityIncidentId: optionalPgId(row.quality_incident_id),
      qualityIncidentRepairScrap: row.repair_scrap,
      qualityIncidentOpen: row.quality_incident_id != null && row.resolution_date == null,
    })),
    intake: intake.rows.map((row) => ({
      id: Number(row.id),
      receivedAt: dateOnlyText(row.received_at),
      unitGroup: row.unit_group,
      unitModel: row.unit_model,
      unitSerial: row.unit_serial,
      pcbaLocationId:
        row.unit_serial == null
          ? null
          : (pcbaLocationBySerial.get(row.unit_serial.trim().toUpperCase()) ?? null),
      visualInspection: row.visual_inspection,
      dateOfManufacture: dateOnlyText(row.date_of_manufacture),
      aaVoltage: row.aa_voltage,
      cVoltage: row.c_voltage,
      hybridCap: row.hybrid_cap,
      firmwareVersion: row.firmware_version,
      boardRevision: row.board_revision,
      loggedBy: row.logged_by,
      boxLabel: row.box_label,
      rcaDate: dateOnlyText(row.rca_date),
      rcaNotes: row.rca_notes,
      resolution: row.resolution,
      analyzedBy: row.analyzed_by,
      resolutionDate: dateOnlyText(row.resolution_date),
      resolvedBy: row.resolved_by,
      createdAt: isoTimestampText(row.created_at) ?? "",
      updatedAt: isoTimestampText(row.updated_at) ?? "",
    })),
    rcas: rcas.rows.map((row) => ({
      id: Number(row.id),
      rmaId: Number(row.rma_id),
      status: row.status === "closed" ? "closed" : "open",
      openedAt: isoTimestampText(row.opened_at) ?? "",
      openedBy: row.opened_by,
      openedByDisplay: row.opened_by_display,
      openedByAvatarUrl: row.opened_by_avatar_url,
      openedAutomatically: row.opened_automatically === true,
      summary: row.summary,
      form: row.form != null && typeof row.form === "object" && !Array.isArray(row.form) ? row.form : null,
    })),
    investigations: investigations.rows.map((row) => ({
      investigationId: Number(row.investigation_id),
      qualityCaseId: Number(row.quality_case_id),
      investigationStatus: row.investigation_status,
      caseTitle: row.case_title,
    })),
    upsLabels: upsLabels.rows.map((row) => ({
      id: Number(row.id),
      trackingNumber: row.tracking_number,
      serviceCode: row.service_code,
      createdBy: row.created_by,
      createdAt: isoTimestampText(row.created_at) ?? "",
      voidedAt: isoTimestampText(row.voided_at),
    })),
    rcaLinkedRmas: rcaLinkedRmas.rows.map((row) => ({
      rcaId: Number(row.rca_id),
      id: Number(row.id),
      rmaNumber: row.rma_number,
      status: presentRmaStatus(row.status),
      customerName: row.customer_name,
      contactName: row.contact_name,
      contactPhone: row.contact_phone,
      dateReceived: dateOnlyText(row.date_received),
      quantityAndModels: row.quantity_and_models,
      complaint: row.complaint,
    })),
  };
}

export { parseOptionalId, parseOptionalVoltage, trimOptionalText, trimRequiredText };
